import React from 'react';
import UseCasePageTemplate from '../../../components/UseCasePageTemplate';
import PipelineAnimation from '../../../components/PipelineAnimation';

const DataEngineerPage = () => {
  // Engineer icon
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  );

  // Replace the static heroSvg with PipelineAnimation component wrapper
  const heroSvg = (
    <div className="w-full max-w-3xl mx-auto">
      <PipelineAnimation />
    </div>
  );

  // Icons for solutions
  const pipelineIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  );

  const monitoringIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );

  const automationIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );

  const challenges = [
    {
      title: "Complex Pipeline Management",
      description: "Managing numerous complex data pipelines across different platforms while ensuring reliability and performance."
    },
    {
      title: "Reactive Troubleshooting",
      description: "Spending excessive time troubleshooting data pipeline failures after they've already impacted downstream systems."
    },
    {
      title: "Testing and Validation at Scale",
      description: "Implementing comprehensive testing and validation across all data assets without creating maintenance bottlenecks."
    },
    {
      title: "Pipeline Debt",
      description: "Accumulating technical debt as new data sources and transformations are added without proper documentation or standardization."
    }
  ];

  const solutions = [
    {
      icon: pipelineIcon,
      title: "Pipeline Health Monitoring",
      description: "Comprehensive monitoring of your data pipelines with alerts for performance issues, failures, and anomalies.",
      items: [
        "End-to-end pipeline visibility with detailed execution metrics",
        "Automated detection of pipeline bottlenecks and inefficiencies",
        "Proactive alerts for pipeline health issues",
        "Historical performance analytics and trend reporting",
        "Infrastructure resource utilization insights"
      ]
    },
    {
      icon: monitoringIcon,
      title: "Scalable Data Quality Testing",
      description: "Implement and automate data quality checks within your pipelines without adding significant overhead.",
      items: [
        "Built-in testing framework that integrates with your pipeline code",
        "Version-controlled quality rules and expectations",
        "Customizable quality checks for specific business requirements",
        "Pre-built test templates for common validation scenarios",
        "Regression testing for data transformations"
      ]
    },
    {
      icon: automationIcon,
      title: "Automated Lineage and Documentation",
      description: "Reduce manual documentation with automated lineage tracking and impact analysis.",
      items: [
        "Auto-discovery of pipeline dependencies and data flows",
        "Visual lineage maps showing relationships between assets",
        "Automated documentation of schema changes",
        "Impact analysis for proposed pipeline changes",
        "Metadata management and cataloging"
      ]
    }
  ];

  const benefits = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: "Reduced Troubleshooting Time",
      description: "Identify and fix pipeline issues faster with detailed diagnostics and root cause analysis tools."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      title: "Streamlined Pipeline Development",
      description: "Build new pipelines faster with standardized templates, automated testing, and best practice recommendations."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      ),
      title: "Enhanced Collaboration",
      description: "Improve collaboration with data analysts and business users through shared visibility into data assets."
    }
  ];

  const testimonial = {
    quote: "Qupid has revolutionized how we manage our data pipelines. The visibility and automated testing have reduced our troubleshooting time by 60% and allowed us to focus on building new features rather than maintaining existing ones.",
    author: "Alex Chen",
    title: "Senior Data Engineer",
    company: "Tech Innovations Inc."
  };

  const relatedUsesCases = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      title: "For Data Leaders",
      description: "Discover how Qupid helps Data Leaders gain visibility and drive strategic value.",
      link: "/solutions/by-persona/data-leader"
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      title: "For Data Users",
      description: "Learn how Qupid empowers Data Users with reliable and accurate data.",
      link: "/solutions/by-persona/data-user"
    }
  ];

  return (
    <UseCasePageTemplate
      title="For Data Engineers"
      description="Build, monitor, and maintain reliable data pipelines with automated quality checks, lineage tracking, and proactive alerts."
      heroSvg={heroSvg}
      icon={icon}
      challenges={challenges}
      solutions={solutions}
      benefits={benefits}
      testimonial={testimonial}
      relatedUsesCases={relatedUsesCases}
    />
  );
};

export default DataEngineerPage; 